import React, { useState, useEffect } from "react";
import { Phone } from "lucide-react";

const SocialMediaIcons = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredIcon, setHoveredIcon] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 200) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const containerStyle = {
    position: "fixed",
    bottom: "30px",
    right: "30px",
    display: isVisible ? "flex" : "none",
    flexDirection: "column",
    alignItems: "center",
    zIndex: 1000,
    gap: "16px",
  };

  const getIconStyle = (type, isHovered) => ({
    width: "64px",
    height: "64px",
    borderRadius: "50%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
    transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
    transform: isHovered ? "scale(1.15) translateY(-4px)" : "scale(1)",
    background:
      type === "phone"
        ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
        : "linear-gradient(135deg, #25d366 0%, #128c7e 100%)",
    boxShadow: isHovered
      ? type === "phone"
        ? "0 20px 40px rgba(102, 126, 234, 0.4), 0 8px 16px rgba(102, 126, 234, 0.3)"
        : "0 20px 40px rgba(37, 211, 102, 0.4), 0 8px 16px rgba(37, 211, 102, 0.3)"
      : type === "phone"
      ? "0 8px 25px rgba(102, 126, 234, 0.25), 0 4px 10px rgba(102, 126, 234, 0.15)"
      : "0 8px 25px rgba(37, 211, 102, 0.25), 0 4px 10px rgba(37, 211, 102, 0.15)",
    backdropFilter: "blur(10px)",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    position: "relative",
    overflow: "hidden",
  });

  const iconInnerStyle = {
    color: "white",
    width: "28px",
    height: "28px",
    transition: "all 0.3s ease",
    filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))",
    zIndex: 2,
    position: "relative",
  };

  const glowStyle = (type, isHovered) => ({
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: isHovered ? "120%" : "0%",
    height: isHovered ? "120%" : "0%",
    borderRadius: "50%",
    background:
      type === "phone"
        ? "radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%)"
        : "radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%)",
    transition: "all 0.6s ease",
    opacity: isHovered ? 1 : 0,
    pointerEvents: "none",
  });

  const pulseRingStyle = (type) => ({
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "100%",
    height: "100%",
    borderRadius: "50%",
    border: `2px solid ${type === "phone" ? "#667eea" : "#25d366"}`,
    opacity: 0,
    animation: "pulse 2s infinite ease-out",
  });

  const handleCall = () => {
    window.open("tel:+919202340761", "_self");
  };

  const handleWhatsApp = () => {
    window.open("https://wa.me/+919202340761", "_blank");
  };

  return (
    <>
      <style jsx>{`
        @keyframes pulse {
          0% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 0.7;
          }
          100% {
            transform: translate(-50%, -50%) scale(1.4);
            opacity: 0;
          }
        }

        @keyframes float {
          0%,
          100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-8px);
          }
        }

        .float-animation {
          animation: float 3s ease-in-out infinite;
        }
      `}</style>

      <div style={containerStyle} className="float-animation">
        {/* Phone Icon */}
        <div
          style={getIconStyle("phone", hoveredIcon === "phone")}
          onMouseEnter={() => setHoveredIcon("phone")}
          onMouseLeave={() => setHoveredIcon(null)}
          onClick={handleCall}
        >
          <div style={glowStyle("phone", hoveredIcon === "phone")} />
          <div style={pulseRingStyle("phone")} />
          <Phone style={iconInnerStyle} />
        </div>

        {/* WhatsApp Icon */}
        <div
          style={getIconStyle("whatsapp", hoveredIcon === "whatsapp")}
          onMouseEnter={() => setHoveredIcon("whatsapp")}
          onMouseLeave={() => setHoveredIcon(null)}
          onClick={handleWhatsApp}
        >
          <div style={glowStyle("whatsapp", hoveredIcon === "whatsapp")} />
          <div style={pulseRingStyle("whatsapp")} />
          <svg style={iconInnerStyle} viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.465 3.516" />
          </svg>
        </div>
      </div>
    </>
  );
};

export default SocialMediaIcons;
